import React from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { colors } from "style";
import Card from "components/Card";
import { InterviewTitle, InterviewSubtitle } from "components/VideoPresentation";

/**
 * FinishedStep Component
 *
 * @param {Array} questions - List of questions
 * @param {Array} answers - [{ questionId, videoUrl, thumbnailUrl }]
 */
const FinishedStep = ({ questions, answers }) => (
  <div css={styles.container}>
    <InterviewTitle titleId="VideoPresentation.FinishedStep.PageTitle" />
    <InterviewSubtitle subtitleId="VideoPresentation.FinishedStep.PageSubtitle" />

    <Card css={styles.card}>
      <div css={styles.badge}>
        <FormattedMessage id="VideoPresentation.FinishedStep.Badge" />
      </div>

      <div css={styles.grid}>
        {questions.map((q) => {
          const answer = answers.find((a) => a.questionId === q.id || a.id === q.id);

          return (
            <div key={q.id} css={styles.answer_card}>
              <div css={styles.item_header}>
                <span css={styles.question_title}>{q.title}</span>
                <span css={answer ? styles.submitted : styles.missing}>{answer ? "Submitted" : "Missing"}</span>
              </div>

              {answer?.videoUrl ? (
                <video src={answer.videoUrl} controls css={styles.video_preview} poster={answer.thumbnailUrl} />
              ) : (
                <div css={styles.no_video_box}>
                  <FormattedMessage id="VideoPresentation.FinishedStep.NoVideoSubmitted" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  </div>
);

const styles = {
  container: css`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  card: css`
    padding: 24px;
    border-radius: 20px;
    box-shadow: 0 8px 20px rgba(17, 24, 39, 0.06);
    display: flex;
    flex-direction: column;
    gap: 24px;
  `,
  badge: css`
    padding: 4px 12px;
    align-self: flex-start;
    border-radius: 999px;
    background: ${colors.purpleRainLight3};
    border: 1px solid ${colors.purpleRainDark2};
    font-size: 12px;
    font-weight: 600;
    color: ${colors.purpleRainBase};
    text-transform: uppercase;
  `,
  grid: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;

    @media (max-width: 760px) {
      grid-template-columns: 1fr;
    }
  `,
  answer_card: css`
    padding: 16px;
    border-radius: 16px;
    border: 1px solid ${colors.grayAnatomyLight3};
    background: ${colors.grayAnatomyLight7};
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  item_header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  question_title: css`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.grayAnatomyDark};
  `,
  submitted: css`
    color: ${colors.green};
    font-weight: 600;
  `,
  missing: css`
    color: ${colors.red};
    font-weight: 600;
  `,
  video_preview: css`
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 12px;
    background: ${colors.black};
  `,
  no_video_box: css`
    background: ${colors.grayAnatomyLight4};
    padding: 20px;
    border-radius: 12px;
    text-align: center;
    font-size: 14px;
    color: ${colors.grayAnatomyBase};
  `,
};

FinishedStep.propTypes = {
  questions: PropTypes.array.isRequired,
  answers: PropTypes.array.isRequired,
};

export default FinishedStep;
